import React, {useEffect, useMemo, useRef, useState,} from "react";
import {useLocation, useNavigate,} from "react-router-dom";
import {Search, Loader2, Send, ArrowLeft, RotateCcw,} from "lucide-react";
import { Translator } from "./translator";

const BASE = "https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com";

const deviceId = (() => {
  const k = "teche_device_id";
  let id = null;
  try {
    id = localStorage.getItem(k);
  } catch {}
  if (!id) {
    const rnd =
      crypto.randomUUID?.() ||
      (Date.now() + "-" + Math.random());
    try {
      localStorage.setItem(k, rnd);
    } catch {}
    id = rnd;
  }
  return id;
})();

// --- helpers ---------------------------------------------------------

function groupByDay(list) {
  const fmt = (d) =>
    new Date(+d).toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const map = [];
  let cur = null;
  let curKey = null;

  list.forEach((m) => {
    // 🛡️ guard against undefined / missing createdAt
    if (!m || m.createdAt == null) return;

    const k = fmt(m.createdAt);
    if (k !== curKey) {
      curKey = k;
      cur = { key: k, items: [] };
      map.push(cur);
    }
    cur.items.push(m);
  });

  return map;
}


function previewOf(item) {
  const p = (item.preview ?? item.lastMessage ?? "").trim();
  return p || "";
}

function isMine(msg, me) {
  return (
    msg.senderEmail?.toLowerCase() ===
    me?.email?.toLowerCase()
  );
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(
    () => new URLSearchParams(search),
    [search]
  );
}

// --------------------------------------------------------------------

export default function ChatPage() {
  const navigate = useNavigate();
  const query = useQuery();
  const convIdFromUrl = query.get("convId") || null;

  // --- i18n: fixed IT / EN / FR, Translator for others -------------
  const { translateText, lang } = Translator();

  const baseTextByLang = useMemo(
    () => ({
      it: {
        titleMessages: "Messaggi",
        loginSubtitle: "Accedi per usare i messaggi.",
        chatsTitle: "Chat",
        searchPlaceholder: "Cerca nelle chat",
        searching: "Ricerca in corso…",
        noResults: "Nessun risultato",
        noChats: "Nessuna chat",
        noChatsYet: "Nessuna chat ancora",
        loadMore: "Carica altri",
        selectChatTitle: "Messaggi",
        selectChatSubtitle:
          "Seleziona una chat a sinistra per iniziare a scrivere.",
        loadPrev: "Carica i messaggi precedenti",
        readMore: "Leggi tutto",
        failedToSend: "Invio non riuscito",
        noMessagesYet: "Ancora nessun messaggio. Saluta 👋",
        inputPlaceholder: "Scrivi un messaggio…",
        emptySayHi: "Saluta 👋",
        userFallbackName: "Utente",
        backToChats: "Torna alle chat",
        convDetails: "Dettagli della conversazione",
        privacyTitle: "Privacy",
        privacyBody:
          "I messaggi su Te.C.H-e utilizzano un trasporto sicuro. Solo le persone in questa chat possono vedere i messaggi.",
        shortcutsTitle: "Scorciatoie",
        shortcutReport: "• Segnala conversazione",
        shortcutBlock: "• Blocca utente",
        shortcutProfile: "• Vedi profilo",
      },

      en: {
        titleMessages: "Messages",
        loginSubtitle: "Please log in to use messages.",
        chatsTitle: "Chats",
        searchPlaceholder: "Search Messenger",
        searching: "Searching…",
        noResults: "No results",
        noChats: "No chats",
        noChatsYet: "No chats yet",
        loadMore: "Load more",
        selectChatTitle: "Messages",
        selectChatSubtitle:
          "Select a chat on the left to start messaging.",
        loadPrev: "Load previous messages",
        readMore: "Read more",
        failedToSend: "Failed to send",
        noMessagesYet: "No messages yet. Say hi 👋",
        inputPlaceholder: "Write a message…",
        emptySayHi: "Say hi 👋",
        userFallbackName: "User",
        backToChats: "Back to chats",
        convDetails: "Conversation details",
        privacyTitle: "Privacy",
        privacyBody:
          "Messages on Te.C.H-e use secure transport. Only people in this chat can see the messages.",
        shortcutsTitle: "Shortcuts",
        shortcutReport: "• Report conversation",
        shortcutBlock: "• Block user",
        shortcutProfile: "• View profile",
      },

      fr: {
        titleMessages: "Messages",
        loginSubtitle:
          "Connectez-vous pour utiliser les messages.",
        chatsTitle: "Discussions",
        searchPlaceholder: "Rechercher dans les messages",
        searching: "Recherche en cours…",
        noResults: "Aucun résultat",
        noChats: "Aucune discussion",
        noChatsYet: "Aucune discussion pour le moment",
        loadMore: "Charger plus",
        selectChatTitle: "Messages",
        selectChatSubtitle:
          "Sélectionnez une discussion à gauche pour commencer à écrire.",
        loadPrev: "Charger les messages précédents",
        readMore: "Lire la suite",
        failedToSend: "Échec de l’envoi",
        noMessagesYet:
          "Pas encore de messages. Dites bonjour 👋",
        inputPlaceholder: "Écrire un message…",
        emptySayHi: "Dites bonjour 👋",
        userFallbackName: "Utilisateur",
        backToChats: "Retour aux discussions",
        convDetails: "Détails de la conversation",
        privacyTitle: "Confidentialité",
        privacyBody:
          "Les messages sur Te.C.H-e utilisent un transport sécurisé. Seules les personnes de cette discussion peuvent voir les messages.",
        shortcutsTitle: "Raccourcis",
        shortcutReport: "• Signaler la conversation",
        shortcutBlock: "• Bloquer l’utilisateur",
        shortcutProfile: "• Voir le profil",
      },
    }),
    []
  );

  const [t, setT] = useState(baseTextByLang.en); // default

  useEffect(() => {
    // Fixed strings for it/en/fr
    if (lang === "it" || lang === "en" || lang === "fr") {
      setT(baseTextByLang[lang] || baseTextByLang.en);
      return;
    }

    // Other languages → translate from Italian
    let cancelled = false;
    (async () => {
      try {
        const source = baseTextByLang.it;
        const entries = Object.entries(source);
        const translatedEntries = await Promise.all(
          entries.map(async ([key, value]) => {
            const out = await translateText(value);
            return [key, out || value];
          })
        );
        if (!cancelled) {
          setT(Object.fromEntries(translatedEntries));
        }
      } catch {
        if (!cancelled) {
          setT(baseTextByLang.en);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseTextByLang]);


  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsMobile(
        typeof window !== "undefined" &&
          window.innerWidth < 768
      );
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // conversations
  const [convos, setConvos] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingConvos, setLoadingConvos] =
    useState(true);

  // search
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  // active thread
  const [active, setActive] = useState(null); // {id, name, avatar, lastReadAt,...}
  const [msgs, setMsgs] = useState([]);
  const [before, setBefore] = useState(null);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  // meta / info panel
  const [meta, setMeta] = useState({
    name: "Chat",
    avatar: "/avatar.png",
    lastReadAt: 0,
  });

  // reduce load when tab is not visible
  const [isVisible, setIsVisible] = useState(
    typeof document === "undefined"
      ? true
      : document.visibilityState === "visible"
  );

  // visibility listener
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      setIsVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);


  // -------------------------------------------------------------
  // load conversations
  // -------------------------------------------------------------
  const loadConvos = async (extra = {}, { silent } = { silent: false }) => {
    if (!me?.email) return;
    if (!silent) setLoadingConvos(true);
    try {
      const url = new URL(`${BASE}/conversations_list`);
      url.searchParams.set("email", me.email);
      if (extra.cursor) {
        url.searchParams.set("cursor", extra.cursor);
      }
      const data = await fetch(url)
        .then((r) => r.json())
        .catch(() => ({ items: [], nextCursor: null }));
      if (extra.append) {
        setConvos((prev) => [
          ...prev,
          ...(data.items || []),
        ]);
      } else {
        setConvos(data.items || []);
      }
      setCursor(data.nextCursor || null);
    } finally {
      if (!silent) setLoadingConvos(false);
    }
  };

  const loadMoreConvos = () => {
    if (!cursor) return;
    loadConvos({ append: true, cursor });
  };

  // initial load
  useEffect(() => {
    if (!me?.email) return;
    loadConvos();
  }, [me?.email]);

  // friend request accepted → open that conversation
  useEffect(() => {
    const h = (e) => {
      const cid = e?.detail?.convId;
      if (!cid) return;
      loadConvos();
      navigate(
        `/messages?convId=${encodeURIComponent(cid)}`
      );
    };
    window.addEventListener("friends:accepted", h);
    return () =>
      window.removeEventListener("friends:accepted", h);
  }, [navigate, me?.email]);

  // -------------------------------------------------------------
  // search across conversations
  // -------------------------------------------------------------
  useEffect(() => {
    if (!me?.email) return;
    if (!q.trim()) {
      setResults([]);
      setSearching(false);
      return;
    }
    const id = setTimeout(async () => {
      setSearching(true);
      try {
        const url = new URL(
          `${BASE}/conversations_search`
        );
        url.searchParams.set("email", me.email);
        url.searchParams.set("q", q.trim());
        url.searchParams.set("limit", "30");
        const data = await fetch(url)
          .then((r) => r.json())
          .catch(() => ({ items: [] }));
        setResults(data.items || []);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [q, me?.email]);

  // -------------------------------------------------------------
  // load messages + meta for active conv
  // -------------------------------------------------------------
  const applyMetaFromList = (cid) => {
    const row = convos.find((c) => c.id === cid);
    if (!row) return;
    setMeta({
      name: row.name || "Chat",
      avatar: row.avatar || row.image || "/avatar.png",
      lastReadAt: row.lastReadAt || 0,
    });
  };

  const loadMessages = async (cid, olderThan = null) => {
    if (!cid) return;
    const url = new URL(`${BASE}/messages_page`);
    url.searchParams.set("convId", cid);
    if (olderThan)
      url.searchParams.set("before", olderThan);
    const data = await fetch(url)
      .then((r) => r.json())
      .catch(() => ({ items: [] }));
    const arr = (data.items || []).reverse();
    setMsgs((prev) =>
      olderThan ? [...arr, ...prev] : arr
    );
    setBefore(arr.length ? arr[0].createdAt : null);

    if (!olderThan && me?.email) {
      fetch(
        `${BASE}/messages_read?convId=${encodeURIComponent(
          cid
        )}&email=${encodeURIComponent(
          me.email
        )}&upToCreatedAt=${Date.now()}`
      ).catch(() => {});
      // To locally mark this conversation as read in sidebar
      setConvos((prev) =>
        prev.map((c) =>
          c.id === cid
            ? { ...c, unread: 0, lastReadAt: Date.now() }
            : c
        )
      );
    }
  };

  // open a conversation (click list or from URL)
  const openConversation = (c) => {
    if (!c?.id) return;
    const cid = c.id;
    setActive(c);
    applyMetaFromList(cid);
    loadMessages(cid, null);
    navigate(
      `/messages?convId=${encodeURIComponent(cid)}`,
      { replace: false }
    );
  };

  // when URL changes convId → open it
  useEffect(() => {
    if (!convIdFromUrl) {
      setActive(null);
      setMsgs([]);
      setBefore(null);
      return;
    }
    const row =
      convos.find((c) => c.id === convIdFromUrl) ||
      { id: convIdFromUrl };
    setActive(row);
    applyMetaFromList(convIdFromUrl);
    loadMessages(convIdFromUrl, null);
  }, [convIdFromUrl, convos]);

  // scroll to bottom when msgs change
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [msgs.length, active?.id]);

  // long-poll for new messages
  useEffect(() => {
    if (!active?.id || !isVisible) return;
    let aborted = false;

    let since =
      Array.isArray(msgs) && msgs.length
        ? Math.max(
            ...msgs
              .map((m) => m && m.createdAt)
              .filter((v) => v != null)
          )
        : 0;

    const sleep = (ms) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    const loop = async () => {
      while (!aborted) {
        try {
          const res = await fetch(
            `${BASE}/live_events?convId=${active.id}&since=${since}`,
            {
              // server can hold this up to ~30s
              signal:
                AbortSignal.timeout?.(30000) ??
                undefined,
            }
          );

          if (!res.ok) throw new Error("live_events failed");

          const ev = await res.json();
          since = ev.now || since || Date.now();

          const newMessages = ev.newMessages || [];
          if (newMessages.length) {
            setMsgs((prev) => {
              const seen = new Set(prev.map((m) => m.id));
              const fresh = newMessages.filter(
                (m) => m && !seen.has(m.id)
              );
              if (!fresh.length) return prev;
              return [...prev, ...fresh];
            });
            continue;
          }
        } catch (e) {
          console.error("live_events error", e);
        }
        await sleep(1000);
      }
    };

    loop();

    return () => {
      aborted = true;
    };
  }, [active?.id, isVisible]); // deliberately *not* depending on msgs


  // -------------------------------------------------------------
  // send message
  // -------------------------------------------------------------
  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || !me?.email || !active?.id) return;
    setSending(true);
    setText("");

    const dedupeKey = `${deviceId}:${Date.now()}`;
    const optimistic = {
      id: dedupeKey,
      convId: active.id,
      senderEmail: me.email,
      text: trimmed,
      createdAt: Date.now(),
    };
    setMsgs((prev) => [...prev, optimistic]);

    try {
      const body = new URLSearchParams({
        convId: active.id,
        senderEmail: me.email,
        text: trimmed,
        dedupeKey,
      });

      const res = await fetch(
        `${BASE}/messages_send`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/x-www-form-urlencoded",
          },
          body,
        }
      );
      const data = await res.json();

      if (!res.ok || data.error || !data.msg) {
        console.error("messages_send error", data);
        setMsgs((prev) =>
          prev.map((m) =>
            m.id === dedupeKey
              ? { ...m, failed: true }
              : m
          )
        );
      } else {
        setMsgs((prev) =>
          prev.map((m) =>
            m.id === dedupeKey ? data.msg : m
          )
        );
      }
    } catch {
      setMsgs((prev) =>
        prev.map((m) =>
          m.id === dedupeKey
            ? { ...m, failed: true }
            : m
        )
      );
    } finally {
      setSending(false);
    }
  };




  // -------------------------------------------------------------
  // resend failed message
  // -------------------------------------------------------------
  const handleRetry = async (msg) => {
    if (!msg?.id || !msg.text || !me?.email || !active?.id) return;

    const dedupeKey = msg.id; // reuse same id as dedupeKey for idempotency

    // remove "failed" flag optimistically
    setMsgs((prev) =>
      prev.map((m) =>
        m.id === msg.id ? { ...m, failed: false } : m
      )
    );

    try {
      const body = new URLSearchParams({
        convId: active.id,
        senderEmail: me.email,
        text: msg.text,
        dedupeKey,
      });

      const res = await fetch(`${BASE}/messages_send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      });
      const data = await res.json();

      if (!res.ok || data.error || !data.msg) {
        console.error("messages_send retry error", data);
        setMsgs((prev) =>
          prev.map((m) =>
            m.id === msg.id ? { ...m, failed: true } : m
          )
        );
      } else {
        setMsgs((prev) =>
          prev.map((m) =>
            m.id === msg.id ? data.msg : m
          )
        );
      }
    } catch (e) {
      console.error("messages_send retry exception", e);
      setMsgs((prev) =>
        prev.map((m) =>
          m.id === msg.id ? { ...m, failed: true } : m
        )
      );
    }
  };


  // -------------------------------------------------------------
  // RENDER PIECES
  // -------------------------------------------------------------

  const listToShow =
    q.trim().length > 0 ? results : convos;

  const renderConversationRow = (item) => {
    const hasUnread = (item.unread || 0) > 0;
    const isActive = item.id === active?.id;
    const subtitle = previewOf(item);
    return (
      <button
        key={item.id}
        onClick={() => openConversation(item)}
        className={
          "w-full flex items-center gap-3 px-3 py-2 text-left border-b last:border-b-0 " +
          (isActive
            ? "bg-blue-50"
            : "hover:bg-gray-50")
        }
      >
        <div className="relative">
          <img
            src={
              item.avatar ||
              item.image ||
              "/avatar.png"
            }
            alt=""
            className="w-10 h-10 rounded-full object-cover"
          />
          {item.online && (
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border border-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div
              className={
                "text-sm truncate " +
                (hasUnread
                  ? "font-semibold text-gray-900"
                  : "font-medium")
              }
            >
              {item.name || t.userFallbackName}
            </div>
            {hasUnread && (
              <span className="ml-auto w-2 h-2 rounded-full bg-blue-600" />
            )}
          </div>
          <div
            className={
              "text-xs truncate " +
              (hasUnread
                ? "text-gray-900 font-medium"
                : "text-gray-600")
            }
          >
            {subtitle || t.emptySayHi}
          </div>
        </div>
      </button>
    );
  };

  const renderMessages = () => {
    if (!active?.id) {
      return (
        <div className="flex-1 flex items-center justify-center text-center text-gray-500">
          <div>
            <div className="text-2xl font-semibold mb-2">
              {t.selectChatTitle}
            </div>
            <div className="text-sm">
              {t.selectChatSubtitle}
            </div>
          </div>
        </div>
      );
    }


    const buckets = groupByDay(
      Array.isArray(msgs)
        ? msgs.filter((m) => m && m.createdAt != null)
        : []
    );

    return (
      <div
        ref={scrollRef}
        className="flex-[0.5] overflow-auto px-4 py-2"
      >
        {before && (
          <div className="flex justify-center mb-2">
            <button
              onClick={() => loadMessages(active.id, before)}
              className="text-xs text-[#324B8E] hover:underline"
            >
              {t.loadPrev}
            </button>
          </div>
        )}

        {buckets.map((b) => (
          <div key={b.key}>
            <div className="my-2 text-[11px] text-gray-500 text-center">
              {b.key}
            </div>
            {b.items.map((m) => {
              const mine = isMine(m, me);
              const longText =
                (m.text || "").length > 260;
              return (
                <div
                  key={m.id}
                  className={
                    "mb-2 flex " +
                    (mine
                      ? "justify-end"
                      : "justify-start")
                  }
                >
                  <div
                    className={
                      "max-w-[80%] rounded-2xl px-3 py-2 text-sm break-words " +
                      (mine
                        ? "bg-[#324B8E] text-white"
                        : "bg-gray-100 text-gray-900")
                    }
                  >
                    <div className="whitespace-pre-wrap">
                      {longText ? (
                        <details>
                          <summary className="cursor-pointer">
                            {t.readMore}
                          </summary>
                          {m.text}
                        </details>
                      ) : (
                        m.text
                      )}
                    </div>
                    <div className="text-[10px] opacity-70 mt-1 text-right">
                      {new Date(
                        Number(m.createdAt)
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    {m.failed && (
                      <button
                        type="button"
                        onClick={() => handleRetry(m)}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] text-red-500 hover:text-red-600"
                      >
                        <span>{t.failedToSend}</span>
                        <RotateCcw className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {!msgs.length && (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            {t.noMessagesYet}
          </div>
        )}
      </div>
    );
  };

  const renderComposer = () => {
    if (!active?.id) return null;
    return (
      <div className="border-t px-3 py-2 flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey
            ) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={t.inputPlaceholder}
          className="flex-1 border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300"
        />
        <button
          onClick={handleSend}
          disabled={sending || !text.trim()}
          className="p-2 rounded-xl bg-[#324B8E] text-white disabled:opacity-50"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </div>
    );
  };

  // -------------------------------------------------------------
  // MAIN RENDER
  // -------------------------------------------------------------
  if (!me?.email) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow p-6 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold mb-2">
            {t.titleMessages}
          </h1>
          <p className="text-sm text-gray-600">
            {t.loginSubtitle}
          </p>
        </div>
      </div>
    );
  }

  // mobile layout: either list OR thread
  if (isMobile) {
    if (!active?.id) {
      // only list
      return (
        <div className="flex flex-col h-screen bg-white">
          <header className="flex items-center justify-between px-3 py-3 border-b">
            <h1 className="text-lg font-semibold">
              {t.chatsTitle}
            </h1>
          </header>
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
              <input
                value={q}
                onChange={(e) =>
                  setQ(e.target.value)
                }
                placeholder={t.searchPlaceholder}
                className="w-full pl-8 pr-2 py-2 text-sm border rounded-xl outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            {searching && (
              <div className="p-3 text-sm text-gray-500">
                {t.searching}
              </div>
            )}
            {!searching &&
              listToShow.map(renderConversationRow)}
            {!searching &&
              !listToShow.length && (
                <div className="p-3 text-sm text-gray-500">
                  {q.trim()
                    ? t.noResults
                    : t.noChatsYet}
                </div>
              )}
            {!searching && cursor && (
              <button
                onClick={loadMoreConvos}
                className="w-full py-2 text-sm text-[#324B8E] hover:underline border-t"
              >
                {t.loadMore}
              </button>
            )}
          </div>
        </div>
      );
    }

    // mobile thread view
    return (
      <div className="flex flex-col h-screen bg-white">
        <header className="flex items-center gap-2 px-3 py-3 border-b">
          <button
            onClick={() =>
              navigate("/messages", {
                replace: true,
              })
            }
            className="p-1 rounded-full hover:bg-gray-100"
            aria-label={t.backToChats}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img
            src={meta.avatar || "/avatar.png"}
            alt=""
            className="w-8 h-8 rounded-full object-cover"
          />
          <div className="flex flex-col min-w-0">
            <div className="text-sm font-semibold truncate">
              {meta.name || t.chatsTitle}
            </div>
          </div>
        </header>
        {renderMessages()}
        {renderComposer()}
      </div>
    );
  }

  // desktop / tablet layout: 3 columns
  return (
    <div className="flex h-screen bg-white">
      {/* LEFT: conversations list */}
      <aside className="w-[320px] border-r flex flex-col">
        <div className="px-3 py-3 border-b">
          <h1 className="text-lg font-semibold mb-2">
            {t.chatsTitle}
          </h1>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
            <input
              value={q}
              onChange={(e) =>
                setQ(e.target.value)
              }
              placeholder="Search Messenger"
              className="w-full pl-8 pr-2 py-2 text-sm border rounded-xl outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {searching && (
            <div className="p-3 text-sm text-gray-500">
              {t.searching}
            </div>
          )}
          {!searching &&
            listToShow.map(renderConversationRow)}
          {!searching &&
            !listToShow.length && (
              <div className="p-3 text-sm text-gray-500">
                {q.trim() ? t.noResults : t.noChatsYet}
              </div>
            )}
          {!searching && cursor && (
            <button
              onClick={loadMoreConvos}
              className="w-full py-2 text-sm text-[#324B8E] hover:underline border-t"
            >
              {t.loadMore}
            </button>
          )}
        </div>
      </aside>

      {/* CENTER: thread */}
      <section className="flex-1 flex flex-col">
        <header className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={meta.avatar || "/avatar.png"}
              alt=""
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">
                {meta.name || t.chatsTitle}
              </div>
            </div>
          </div>
        </header>
        {renderMessages()}
        {renderComposer()}
      </section>

      {/* RIGHT: info panel */}
      <aside className="hidden lg:flex w-[280px] border-l flex-col">
        <div className="px-4 py-4 border-b">
          <div className="flex flex-col items-center text-center">
            <img
              src={meta.avatar || "/avatar.png"}
              alt=""
              className="w-16 h-16 rounded-full object-cover mb-2"
            />
            <div className="font-semibold">
              {meta.name || t.chatsTitle}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {t.convDetails}
            </div>
          </div>
        </div>
        <div className="px-4 py-4 text-sm text-gray-700 space-y-3">
          <div>
            <div className="font-medium mb-1">
              {t.privacyTitle}
            </div>
            <p className="text-xs text-gray-600">
              {t.privacyBody}
            </p>
          </div>
          <div>
            <div className="font-medium mb-1">
              {t.shortcutsTitle}
            </div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>{t.shortcutReport}</li>
              <li>{t.shortcutBlock}</li>
              <li>{t.shortcutProfile}</li>
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}
